import { useCallback, useMemo } from 'react';
import { notification } from 'antd';
import { Popup } from 'utils/popup.utils';
import { intl } from 'components/localization';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import useStores from 'hooks/use-stores.hooks';
import { apiBaseUrl } from 'constants/config';

type SlackOAuthResult = {
  slackEndpointId: string;
  channel: string;
  team: string;
};

type UseSlackOAuthPopupProps = {
  onSuccess: (result: SlackOAuthResult) => void;
  onError?: (error: string) => void;
  endpointId?: string;
};

export const useSlackOAuthPopup = ({ onSuccess, onError, endpointId }: UseSlackOAuthPopupProps) => {
  const organizationId = useCurrentOrganizationId();
  const { authStore } = useStores();

  const authorizationUrl = useMemo(() => {
    const url = new URL(`${apiBaseUrl}/notifications/slack/oauth/start`);
    url.searchParams.append('organizationId', organizationId);
    url.searchParams.append('token', authStore.getAccessToken()!);
    if (endpointId) {
      url.searchParams.append('endpointId', endpointId);
    }
    return url.toString();
  }, [organizationId, authStore, endpointId]);

  const openSlackOAuthPopup = useCallback(() => {
    Popup.open({
      url: authorizationUrl,
      closeByMessage: 'authorizationCallback',
      onBlock: () => {
        const errorMsg = intl.formatMessage({ id: 'organization.settings.notifications.slack-oauth.popup-blocked' });
        notification.error({
          message: intl.formatMessage({ id: 'organization.settings.notifications.slack-oauth.error.title' }),
          description: errorMsg
        });
        onError?.(errorMsg);
      },
      onClosed: result => {
        if (result?.error) {
          const errorMsg = result.error;
          notification.error({
            message: intl.formatMessage({ id: 'organization.settings.notifications.slack-oauth.error.title' }),
            description: errorMsg
          });
          onError?.(errorMsg);
        } else if (result?.slackEndpointId) {
          notification.success({
            message: intl.formatMessage({ id: 'organization.settings.notifications.slack-oauth.success.title' }),
            description: intl.formatMessage(
              { id: 'organization.settings.notifications.slack-oauth.success.description' },
              { channel: result.channel, team: result.team }
            )
          });
          onSuccess(result as SlackOAuthResult);
        } else {
          const errorMsg = intl.formatMessage({
            id: 'organization.settings.notifications.slack-oauth.window-closed'
          });
          notification.error({
            message: intl.formatMessage({ id: 'organization.settings.notifications.slack-oauth.error.title' }),
            description: errorMsg
          });
          onError?.(errorMsg);
        }
      },
      features: { height: 700, width: 700 }
    });
  }, [authorizationUrl, onSuccess, onError]);

  return { openSlackOAuthPopup };
};
